import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  CircularProgress,
  IconButton,
  MenuItem,
  Modal,
  Select,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import copy from "copy-to-clipboard";
import { useDispatch } from "react-redux";
import { ReactComponent as CopyIcon } from "../../../assets/Images/CopyIcon.svg";
import BaseButton from "../Common/Buttons/BaseButton";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import ChatBox from "../Common/ChatBox";
import EmptyState from "../Common/EmptyState";
import { CopyAlert } from "../CommonComponents/CommonComponents";
import CustomScrollbars from "../CommonComponents/CustomScrollbars";
import {
  analyzeSummaryAction,
  analyzeTranscriptAction,
} from "../ProjectSection/CampaignSection/Store/Campaign.action";

const BoxLoader = () => {
  return (
    <>
      <Skeleton variant="text" width="404px" sx={commonStyles.skeletonStyle} />
      <Skeleton variant="text" width="596px" sx={commonStyles.skeletonStyle} />
      <Skeleton variant="text" width="490px" sx={commonStyles.skeletonStyle} />
      <Skeleton variant="text" width="308px" sx={commonStyles.skeletonStyle} />
    </>
  );
};

const CallTranscriptModal = ({ show, onClose, selectedTranscript }) => {
  const dispatch = useDispatch();
  const [copyAlert, setCopyAlert] = useState({
    open: false,
    message: "",
  });
  const [localState, setLocalState] = useState({
    summaryView: false,
    summary: "",
    buttonTrue: false,
    noSummary: false,
    language: "English",
    summarySpin: true,
    showSummary: false,
    messages: [],
    spin: true,
  });

  const {
    summaryView,
    summary,
    buttonTrue,
    language,
    summarySpin,
    showSummary,
    messages,
    spin,
  } = localState;

  useEffect(() => {
    const fetchMessages = async () => {
      const res: any = await dispatch(
        analyzeTranscriptAction(selectedTranscript?.call_uuid)
      );

      setLocalState((prev) => ({
        ...prev,
        messages: res?.transcription || [],
        spin: false,
      }));

      // viewCallSummary("English");
    };

    fetchMessages();
  }, [selectedTranscript?.call_uuid]);

  const viewCallSummary = async (language = localState.language) => {
    setLocalState((prev) => ({
      ...prev,
      summaryView: true,
      buttonTrue: true,
      summarySpin: true,
      showSummary: !prev.showSummary,
    }));

    const res: any = await dispatch(
      analyzeSummaryAction({
        call_uuid: selectedTranscript?.call_uuid,
        language,
      })
    );

    if (res) {
      if (res?.summary) {
        setLocalState((prev) => ({
          ...prev,
          summary: res?.summary,
        }));
      }
    } else {
      setLocalState((prev) => ({
        ...prev,
        noSummary: true,
      }));
    }
    setLocalState((prev) => ({
      ...prev,
      summaryView: true,
      buttonTrue: false,
      summarySpin: false,
    }));
  };

  return (
    <Modal
      open={show}
      onClose={(event, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
    >
      <Box className="transcript-container" sx={{ maxHeight: "98vh" }}>
        <Box className="flex-between transcript-header">
          <Box>
            <Typography className="font-14-600-grey">
              Call Transcript -{" "}
              <Typography component="span" className="font-13-500-grey">
                {selectedTranscript?.phone || selectedTranscript?.phone_number}
              </Typography>
            </Typography>
            <Typography className="font-11-500-blue">
              {selectedTranscript?._id || selectedTranscript?.call_uuid}{" "}
              <Tooltip title="Copy UUID">
                <IconButton
                  size="small"
                  onClick={() => {
                    copy(
                      selectedTranscript?._id || selectedTranscript?.call_uuid
                    );
                    setCopyAlert({ open: true, message: "UUID copied!" });
                  }}
                >
                  {" "}
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            sx={{
              color: "#62656E",
              height: 8,
              width: 8,
              p: 1,
              borderRadius: 0.75,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <CustomScrollbars height={showSummary ? "65vh" : "70vh"}>
          <Box
            sx={{
              height: "100%",
              px: 3,
              position: "relative",
            }}
          >
            {spin ? (
              <CircularProgress className="circular-progress" />
            ) : messages?.length > 0 ? (
              messages.map((message: any, index) => (
                <ChatBox
                  key={index}
                  message={message?.value}
                  isUser={message?.type === "input"}
                />
              ))
            ) : (
              <EmptyState message="No Transcriptions Found" />
            )}
          </Box>
        </CustomScrollbars>

        {messages?.length > 0 && (
          <Box
            sx={{
              minHeight: "100px",
              mx: 3,
              py: 1.5,
              borderTop: "1px solid #CCD2DE",
            }}
          >
            <Box className="flex-between-only">
              <Typography className="font-14-600-grey ">
                {summaryView
                  ? "Summary of the call"
                  : "You can now generate summary of the call"}
              </Typography>
              <Box className="flex-end gap-2">
                <Select
                  value={language}
                  onChange={(e) => {
                    setLocalState((prev) => ({
                      ...prev,
                      language: e.target.value,
                      summaryView: false,
                      showSummary: false,
                    }));
                  }}
                  displayEmpty
                  className="transcript-select"
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  <MenuItem className="font-14-grey" value="English">
                    English
                  </MenuItem>
                  <MenuItem className="font-14-grey" value="German">
                    German
                  </MenuItem>
                  <MenuItem className="font-14-grey" value="Korean">
                    Korean
                  </MenuItem>
                </Select>
                <BaseButton
                  variant="contained"
                  title={showSummary ? "Hide Summary" : "View Summary"}
                  handleClick={() => viewCallSummary(language)}
                  disabled={buttonTrue}
                />
              </Box>
            </Box>
            <Box
              sx={{
                maxHeight: "100px",
                ...commonStyles.hideScroll,
              }}
            >
              {showSummary && (
                <Typography className="font-14-grey" mt={1}>
                  {summarySpin ? <BoxLoader /> : summary}
                </Typography>
              )}
            </Box>
          </Box>
        )}

        <CopyAlert
          open={copyAlert.open}
          onClose={() => setCopyAlert({ ...copyAlert, open: false })}
          alertMessage={copyAlert.message}
          styles={{ mt: 9 }}
        />
      </Box>
    </Modal>
  );
};

export default CallTranscriptModal;
